import { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Plus, Edit, Trash2, CheckCircle, XCircle, Key, Users as UserIcon, Eye, Download, RefreshCw, Terminal } from 'lucide-react';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import {
  getPickupFolders, getPickupFolderById, createPickupFolder, updatePickupFolder, deletePickupFolder,
  getPickupUsers, getPickupUserById, createPickupUser, updatePickupUser, deletePickupUser,
  getAllPickupFolders, getPickupFolderFiles
} from '../services/pickupServiceSwitch';
import { getUsers } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import config from '../config/config';
import './PickupFolders.css';

export default function PickupFolders() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManagerOrAbove = user?.role === 'admin' || user?.role === 'manager';

  const [activeTab, setActiveTab] = useState('folders');
  const [toast, setToast] = useState(null);

  /* ── Folders state ── */
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [foldersError, setFoldersError] = useState(null);
  const [folderSearch, setFolderSearch] = useState('');
  const [folderPage, setFolderPage] = useState(1);
  const [folderTotalPages, setFolderTotalPages] = useState(1);

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showEditFolder, setShowEditFolder] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderFiles, setShowFolderFiles] = useState(false);
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderFilesLoading, setFolderFilesLoading] = useState(false);
  const [browsingFolder, setBrowsingFolder] = useState(null);

  /* ── Users state ── */
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const itemsPerPage = 10;

  /* ── Fetch folders ── */
  const fetchFolders = useCallback(async () => {
    try {
      setFoldersLoading(true);
      setFoldersError(null);
      const response = await getPickupFolders({ search: folderSearch, page: folderPage, limit: itemsPerPage });
      setFolders(response.data);
      setFolderTotalPages(response.pagination.totalPages);
    } catch (err) {
      setFoldersError(err.message || 'Failed to load pickup folders');
    } finally {
      setFoldersLoading(false);
    }
  }, [folderSearch, folderPage]);

  /* ── Fetch users ── */
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const response = await getPickupUsers({ search: userSearch, page: userPage, limit: itemsPerPage });
      setUsers(response.data);
      setUserTotalPages(response.pagination.totalPages);
    } catch (err) {
      setUsersError(err.message || 'Failed to load pickup users');
    } finally {
      setUsersLoading(false);
    }
  }, [userSearch, userPage]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleFolderSearch = (e) => {
    setFolderSearch(typeof e === 'string' ? e : e.target.value);
    setFolderPage(1);
  };

  const handleUserSearch = (e) => {
    setUserSearch(typeof e === 'string' ? e : e.target.value);
    setUserPage(1);
  };

  const handleEditFolder = async (folder) => {
    try {
      const details = await getPickupFolderById(folder.id);
      setSelectedFolder(details);
      setShowEditFolder(true);
    } catch {
      showToastMsg('Failed to load folder details', 'error');
    }
  };

  const handleBrowseFiles = async (folder) => {
    try {
      setBrowsingFolder(folder);
      setFolderFilesLoading(true);
      setShowFolderFiles(true);
      const result = await getPickupFolderFiles(folder.id);
      setFolderFiles(result.files);
    } catch {
      showToastMsg('Failed to load folder files', 'error');
      setShowFolderFiles(false);
    } finally {
      setFolderFilesLoading(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (folder.reportCount > 0) {
      showToastMsg('Cannot delete: folder has reports assigned. Remove report assignments first.', 'error');
      return;
    }
    if (!window.confirm(`Delete "${folder.name}"? This cannot be undone.`)) return;
    try {
      await deletePickupFolder(folder.id);
      showToastMsg('Pickup folder deleted', 'success');
      fetchFolders();
    } catch (err) {
      showToastMsg(err.message || 'Failed to delete folder', 'error');
    }
  };

  const handleEditUser = async (u) => {
    try {
      const details = await getPickupUserById(u.id);
      setSelectedUser(details);
      setShowEditUser(true);
    } catch {
      showToastMsg('Failed to load user details', 'error');
    }
  };

  const handleDeactivateUser = async (u) => {
    if (!window.confirm(`Deactivate user "${u.username}"?`)) return;
    try {
      await deletePickupUser(u.id);
      showToastMsg('User deactivated', 'success');
      fetchUsers();
    } catch (err) {
      showToastMsg(err.message || 'Failed to deactivate user', 'error');
    }
  };

  const showToastMsg = (message, type = 'success') => setToast({ message, type });

  const handleCopySftpCommand = (username) => {
    const endpoint = config.sftp.endpoint;
    const cmd = `sftp -i /path/to/${username}_private_key.pem ${username}@${endpoint}`;
    navigator.clipboard.writeText(cmd).then(() => {
      showToastMsg('SFTP connection command copied to clipboard', 'success');
    }).catch(() => {
      showToastMsg('Failed to copy command', 'error');
    });
  };

  const handleFolderSuccess = (message) => { showToastMsg(message, 'success'); fetchFolders(); };
  const handleUserSuccess = (message) => { showToastMsg(message, 'success'); fetchUsers(); };

  /* ── Folder columns ── */
  const folderColumns = [
    {
      key: 'name', label: 'Name', sortable: false,
      render: (f) => (
        <div className="folder-name-cell"><FolderOpen size={18} /><span>{f.name}</span></div>
      )
    },
    { key: 'description', label: 'Description', sortable: false },
    { key: 'reportCount', label: 'Reports', sortable: false },
    { key: 'userCount', label: 'Users', sortable: false },
    {
      key: 'retentionDays', label: 'Retention', sortable: false,
      render: (f) => <span className="retention-badge">{f.retentionDays}d</span>
    },
    {
      key: 'archiveEnabled', label: 'Archive', sortable: false,
      render: (f) => (
        <span className={`status-badge ${f.archiveEnabled ? 'status-active' : 'status-inactive'}`}>
          {f.archiveEnabled ? <><CheckCircle size={14} /> Enabled</> : <><XCircle size={14} /> Disabled</>}
        </span>
      )
    },
    {
      key: 'isActive', label: 'Status', sortable: false,
      render: (f) => (
        <span className={`status-badge ${f.isActive ? 'status-active' : 'status-inactive'}`}>
          {f.isActive ? <><CheckCircle size={14} /> Active</> : <><XCircle size={14} /> Inactive</>}
        </span>
      )
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (f) => (
        <div className="action-buttons">
          {isManagerOrAbove && (
            <>
              <button className="action-btn view-btn" onClick={() => handleBrowseFiles(f)} title="Browse Files"><Eye size={16} /></button>
              <button className="action-btn edit-btn" onClick={() => handleEditFolder(f)} title="Edit"><Edit size={16} /></button>
              <button className="action-btn archive-btn" onClick={() => handleDeleteFolder(f)} title="Delete"><Trash2 size={16} /></button>
            </>
          )}
        </div>
      )
    }
  ];

  /* ── User columns ── */
  const userColumns = [
    {
      key: 'username', label: 'Username', sortable: false,
      render: (u) => (
        <div className="pickup-user-cell"><Key size={18} /><span>{u.username}</span></div>
      )
    },
    {
      key: 'folderNames', label: 'Assigned Folders', sortable: false,
      render: (u) => (
        <div className="folder-tags">
          {(u.folderNames || []).map((name, i) => (
            <span key={i} className="folder-tag">{name}</span>
          ))}
          {(!u.folderNames || u.folderNames.length === 0) && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)' }}>None</span>}
        </div>
      )
    },
    {
      key: 'isActive', label: 'Status', sortable: false,
      render: (u) => (
        <span className={`status-badge ${u.isActive ? 'status-active' : 'status-inactive'}`}>
          {u.isActive ? <><CheckCircle size={14} /> Active</> : <><XCircle size={14} /> Inactive</>}
        </span>
      )
    },
    {
      key: 'createdAt', label: 'Created', sortable: false,
      render: (u) => <span>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</span>
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (u) => (
        <div className="action-buttons">
          {config.sftp.endpoint && (
            <button className="action-btn view-btn" onClick={() => handleCopySftpCommand(u.username)} title="Copy SFTP connection command"><Terminal size={16} /></button>
          )}
          {isAdmin && (
            <>
              <button className="action-btn edit-btn" onClick={() => handleEditUser(u)} title="Edit"><Edit size={16} /></button>
              <button className="action-btn archive-btn" onClick={() => handleDeactivateUser(u)} title="Deactivate"><Trash2 size={16} /></button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="pickup-folders-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><FolderOpen size={32} /> Pickup Folders</h1>
            <p className="page-subtitle">Manage SFTP pickup folders and users</p>
          </div>
          {activeTab === 'folders' && isManagerOrAbove && (
            <Button variant="primary" onClick={() => setShowCreateFolder(true)} icon={<Plus size={20} />}>Add Folder</Button>
          )}
          {activeTab === 'users' && isAdmin && (
            <Button variant="primary" onClick={() => setShowCreateUser(true)} icon={<Plus size={20} />}>Add User</Button>
          )}
        </div>
      </div>

      <Card className="pickup-card">
        <div className="tab-buttons">
          <button className={`tab-btn ${activeTab === 'folders' ? 'active' : ''}`} onClick={() => setActiveTab('folders')}>
            <FolderOpen size={16} /> Folders
          </button>
          <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
            <UserIcon size={16} /> Users
          </button>
        </div>

        {activeTab === 'folders' && (
          <>
            <div className="pickup-controls">
              <SearchBar value={folderSearch} onChange={handleFolderSearch} placeholder="Search pickup folders..." />
            </div>
            {foldersLoading && <LoadingSpinner />}
            {foldersError && <ErrorMessage message={foldersError} onRetry={fetchFolders} />}
            {!foldersLoading && !foldersError && (
              <>
                <Table columns={folderColumns} data={folders} emptyMessage="No pickup folders found" />
                {folderTotalPages > 1 && <Pagination currentPage={folderPage} totalPages={folderTotalPages} onPageChange={setFolderPage} />}
              </>
            )}
          </>
        )}

        {activeTab === 'users' && (
          <>
            <div className="pickup-controls">
              <SearchBar value={userSearch} onChange={handleUserSearch} placeholder="Search pickup users..." />
            </div>
            {usersLoading && <LoadingSpinner />}
            {usersError && <ErrorMessage message={usersError} onRetry={fetchUsers} />}
            {!usersLoading && !usersError && (
              <>
                <Table columns={userColumns} data={users} emptyMessage="No pickup users found" />
                {userTotalPages > 1 && <Pagination currentPage={userPage} totalPages={userTotalPages} onPageChange={setUserPage} />}
              </>
            )}
          </>
        )}
      </Card>

      {/* Folder Modals */}
      <FolderFormModal isOpen={showCreateFolder} onClose={() => setShowCreateFolder(false)} onSuccess={handleFolderSuccess} mode="create" />
      <FolderFormModal isOpen={showEditFolder} onClose={() => { setShowEditFolder(false); setSelectedFolder(null); }} onSuccess={handleFolderSuccess} mode="edit" folder={selectedFolder} />

      {/* User Modals */}
      <UserFormModal isOpen={showCreateUser} onClose={() => setShowCreateUser(false)} onSuccess={handleUserSuccess} mode="create" />
      <UserFormModal isOpen={showEditUser} onClose={() => { setShowEditUser(false); setSelectedUser(null); }} onSuccess={handleUserSuccess} mode="edit" pickupUser={selectedUser} />

      {/* Folder Files Modal */}
      <Modal isOpen={showFolderFiles} onClose={() => { setShowFolderFiles(false); setFolderFiles([]); setBrowsingFolder(null); }} title={`Files in "${browsingFolder?.name || ''}"`}>
        {folderFilesLoading ? (
          <LoadingSpinner />
        ) : folderFiles.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>No files in this folder yet</p>
        ) : (
          <div className="folder-files-list">
            <table className="files-table">
              <thead>
                <tr><th>Filename</th><th>Size</th><th>Last Modified</th></tr>
              </thead>
              <tbody>
                {folderFiles.map((f, i) => (
                  <tr key={i}>
                    <td>{f.filename}</td>
                    <td>{(f.size / 1024).toFixed(1)} KB</td>
                    <td>{new Date(f.lastModified).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)', marginTop: '0.5rem' }}>{folderFiles.length} file(s)</p>
          </div>
        )}
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}

/** Folder create/edit modal */
function FolderFormModal({ isOpen, onClose, onSuccess, mode, folder }) {
  const { authorisedTeams, activeTeamId } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({ name: '', description: '', retentionDays: '30', archiveEnabled: false, viewerEmails: [], viewerEmailInput: '', teamId: activeTeamId || '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);

  useEffect(() => {
    if (folder && mode === 'edit') {
      setFormData({
        name: folder.name || '',
        description: folder.description || '',
        retentionDays: String(folder.retentionDays || 30),
        archiveEnabled: folder.archiveEnabled || false,
        viewerEmails: folder.viewerEmails || [],
        viewerEmailInput: ''
      });
    } else if (mode === 'create') {
      setFormData({ name: '', description: '', retentionDays: '30', archiveEnabled: false, viewerEmails: [], viewerEmailInput: '', teamId: activeTeamId || '' });
    }
  }, [folder, mode, isOpen, activeTeamId]);

  useEffect(() => {
    if (isOpen) {
      getUsers({ page: 1, limit: 100 })
        .then(result => setAvailableUsers(result.data || []))
        .catch(() => setAvailableUsers([]));
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (mode === 'create' && !formData.teamId) errs.teamId = 'Select a team';
    if (!formData.name.trim()) errs.name = 'Folder name is required';
    const days = parseInt(formData.retentionDays, 10);
    if (isNaN(days) || days < 1 || days > 365) errs.retentionDays = 'Retention must be 1–365 days';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      // Auto-add any pending email in the input field before submitting
      let finalEmails = [...formData.viewerEmails];
      const pendingEmail = (formData.viewerEmailInput || '').trim();
      if (pendingEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingEmail) && !finalEmails.includes(pendingEmail)) {
        finalEmails.push(pendingEmail);
      }
      // eslint-disable-next-line no-unused-vars
      const { viewerEmailInput, viewerEmails, ...submitData } = formData;
      submitData.viewerEmails = finalEmails;
      if (mode === 'create') {
        await createPickupFolder({ ...submitData, retentionDays: parseInt(submitData.retentionDays, 10), archiveEnabled: submitData.archiveEnabled, viewerEmails: submitData.viewerEmails });
        onSuccess('Pickup folder created');
      } else {
        await updatePickupFolder(folder.id, { ...submitData, retentionDays: parseInt(submitData.retentionDays, 10), archiveEnabled: submitData.archiveEnabled, viewerEmails: submitData.viewerEmails });
        onSuccess('Pickup folder updated');
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || `Failed to ${mode} folder` });
    } finally {
      setLoading(false);
    }
  };

  const emailSuggestions = availableUsers.filter(u => {
    if (!u.email) return false;
    if (formData.viewerEmails.includes(u.email)) return false;
    const query = (formData.viewerEmailInput || '').toLowerCase();
    if (!query) return true;
    return (u.name || '').toLowerCase().includes(query) || u.email.toLowerCase().includes(query);
  });

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!loading) { setErrors({}); onClose(); } }}
      title={mode === 'create' ? 'Add Pickup Folder' : 'Edit Pickup Folder'} size="md" closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>{mode === 'create' ? 'Create' : 'Save'}</Button>
        </div>
      }>
      <form onSubmit={handleSubmit} className="sftp-form">
        {/* Team Selector (create only) */}
        {mode === 'create' && realTeams.length > 1 && (
          <div className="form-group">
            <label htmlFor="folder-teamId" className="form-label">Team <span className="required">*</span></label>
            <select id="folder-teamId" name="teamId" value={formData.teamId} onChange={handleChange} className={`form-select ${errors.teamId ? 'error' : ''}`} disabled={loading}>
              <option value="">Select a team</option>
              {realTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {errors.teamId && <span className="error-text">{errors.teamId}</span>}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="folder-name" className="form-label">Folder Name <span className="required">*</span></label>
          <Input id="folder-name" name="name" value={formData.name} onChange={handleChange} placeholder="Finance Reports" error={errors.name} disabled={loading} />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="folder-desc" className="form-label">Description</label>
          <textarea id="folder-desc" name="description" value={formData.description} onChange={handleChange} placeholder="Describe this pickup folder..." className="form-textarea" disabled={loading} rows={3} />
        </div>
        <div className="form-group">
          <label htmlFor="folder-retention" className="form-label">Retention (days) <span className="required">*</span></label>
          <Input id="folder-retention" name="retentionDays" type="number" value={formData.retentionDays} onChange={handleChange} placeholder="30" error={errors.retentionDays} disabled={loading} min="1" max="365" />
          {errors.retentionDays && <span className="error-text">{errors.retentionDays}</span>}
          <p className="field-hint">Files older than this will be automatically removed (1–365)</p>
        </div>
        <div className="form-group">
          <label className="form-label">Archive</label>
          <div className="toggle-switch-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.archiveEnabled}
                onChange={() => setFormData(prev => ({ ...prev, archiveEnabled: !prev.archiveEnabled }))}
                disabled={loading}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">{formData.archiveEnabled ? 'Archiving enabled' : 'Archiving disabled'}</span>
          </div>
          <p className="field-hint">When enabled, delivered files are automatically copied to a date-stamped archive subfolder</p>
        </div>
        <div className="form-group">
          <label className="form-label">Viewer Emails</label>
          <div className="viewer-emails-container">
            <div className="email-tags">
              {formData.viewerEmails.map((email, i) => (
                <span key={i} className="email-tag">
                  {email}
                  <button type="button" className="email-tag-remove" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      viewerEmails: prev.viewerEmails.filter((_, idx) => idx !== i)
                    }));
                  }} disabled={loading}>×</button>
                </span>
              ))}
            </div>
            <div className="email-suggestions-wrapper">
              <div className="email-input-row">
                <Input
                  name="viewerEmailInput"
                  value={formData.viewerEmailInput || ''}
                  onChange={handleChange}
                  placeholder="Search users or type email..."
                  disabled={loading}
                  onFocus={() => setShowEmailSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowEmailSuggestions(false), 200)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const email = (formData.viewerEmailInput || '').trim();
                      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !formData.viewerEmails.includes(email)) {
                        setFormData(prev => ({
                          ...prev,
                          viewerEmails: [...prev.viewerEmails, email],
                          viewerEmailInput: ''
                        }));
                      }
                    }
                  }}
                />
              </div>
              {showEmailSuggestions && emailSuggestions.length > 0 && (
                <div className="email-suggestions-dropdown">
                  {emailSuggestions.map(u => (
                    <div
                      key={u.email}
                      className="email-suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setFormData(prev => ({
                          ...prev,
                          viewerEmails: [...prev.viewerEmails, u.email],
                          viewerEmailInput: ''
                        }));
                        setShowEmailSuggestions(false);
                      }}
                    >
                      <div>
                        <span className="email-suggestion-name">{u.name}</span>
                        <span className="email-suggestion-email">{u.email}</span>
                      </div>
                      <span className="email-suggestion-role">{u.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="field-hint">Users with these email addresses can view and download files from this folder via the web app</p>
        </div>
        {errors.submit && <div className="form-error">{errors.submit}</div>}
      </form>
    </Modal>
  );
}

/** User create/edit modal */
import { generateSshKeyPair, downloadPrivateKey } from '../utils/sshKeyGen';

function UserFormModal({ isOpen, onClose, onSuccess, mode, pickupUser }) {
  const [formData, setFormData] = useState({ username: '', sshPublicKey: '', folderIds: [], isActive: true });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [keyMode, setKeyMode] = useState('paste'); // 'paste' or 'generate'
  const [generating, setGenerating] = useState(false);
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState(null);
  const [privateKeyDownloaded, setPrivateKeyDownloaded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getAllPickupFolders()
        .then(setAvailableFolders)
        .catch(() => setAvailableFolders([]));
    }
  }, [isOpen]);

  useEffect(() => {
    if (pickupUser && mode === 'edit') {
      setFormData({
        username: pickupUser.username || '',
        sshPublicKey: '',
        folderIds: pickupUser.folderIds || [],
        isActive: pickupUser.isActive !== undefined ? pickupUser.isActive : true
      });
    } else if (mode === 'create') {
      setFormData({ username: '', sshPublicKey: '', folderIds: [], isActive: true });
      setKeyMode('paste');
      setGeneratedPrivateKey(null);
      setPrivateKeyDownloaded(false);
    }
  }, [pickupUser, mode, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFolderToggle = (folderId) => {
    setFormData(prev => ({
      ...prev,
      folderIds: prev.folderIds.includes(folderId)
        ? prev.folderIds.filter(id => id !== folderId)
        : [...prev.folderIds, folderId]
    }));
  };

  const handleActiveToggle = () => {
    setFormData(prev => ({ ...prev, isActive: !prev.isActive }));
  };

  const validateSshKey = (key) => {
    if (!key.trim()) return false;
    return key.trim().startsWith('ssh-rsa') || key.trim().startsWith('ssh-ed25519') || key.trim().startsWith('ecdsa-sha2-nistp');
  };

  const handleGenerateKey = async () => {
    try {
      setGenerating(true);
      setErrors(prev => ({ ...prev, sshPublicKey: '' }));
      const comment = formData.username.trim() ? `${formData.username.trim()}@reporthub` : 'pickup-user@reporthub';
      const { publicKey, privateKey } = await generateSshKeyPair(comment);
      setFormData(prev => ({ ...prev, sshPublicKey: publicKey }));
      setGeneratedPrivateKey(privateKey);
      setPrivateKeyDownloaded(false);
    } catch (err) {
      setErrors(prev => ({ ...prev, sshPublicKey: 'Failed to generate key pair. Try pasting a key instead.' }));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPrivateKey = () => {
    if (generatedPrivateKey) {
      downloadPrivateKey(generatedPrivateKey, formData.username.trim() || 'pickup-user');
      setPrivateKeyDownloaded(true);
    }
  };

  const handleKeyModeChange = (newMode) => {
    setKeyMode(newMode);
    setFormData(prev => ({ ...prev, sshPublicKey: '' }));
    setGeneratedPrivateKey(null);
    setPrivateKeyDownloaded(false);
    setErrors(prev => ({ ...prev, sshPublicKey: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.username.trim()) errs.username = 'Username is required';
    if (mode === 'create' && !formData.sshPublicKey.trim()) {
      errs.sshPublicKey = keyMode === 'generate' ? 'Click "Generate Key Pair" first' : 'SSH public key is required';
    }
    if (formData.sshPublicKey.trim() && !validateSshKey(formData.sshPublicKey)) {
      errs.sshPublicKey = 'Key must start with ssh-rsa, ssh-ed25519, or ecdsa-sha2-nistp';
    }
    if (keyMode === 'generate' && generatedPrivateKey && !privateKeyDownloaded) {
      errs.sshPublicKey = 'Please download the private key before saving — it cannot be retrieved later';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      if (mode === 'create') {
        await createPickupUser(formData);
        onSuccess('Pickup user created');
      } else {
        const updateData = { ...formData };
        if (!updateData.sshPublicKey.trim()) delete updateData.sshPublicKey;
        await updatePickupUser(pickupUser.id, updateData);
        onSuccess('Pickup user updated');
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || `Failed to ${mode} user` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!loading) { setErrors({}); onClose(); } }}
      title={mode === 'create' ? 'Add Pickup User' : 'Edit Pickup User'} size="md" closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>{mode === 'create' ? 'Create' : 'Save'}</Button>
        </div>
      }>
      <form onSubmit={handleSubmit} className="sftp-form">
        <div className="form-group">
          <label htmlFor="user-username" className="form-label">Username <span className="required">*</span></label>
          <Input id="user-username" name="username" value={formData.username} onChange={handleChange} placeholder="auditor_finance" error={errors.username} disabled={loading} />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            <Key size={16} /> SSH Key {mode === 'create' && <span className="required">*</span>}
            {mode === 'edit' && <span className="field-hint-inline">(leave blank to keep current)</span>}
          </label>

          {/* Key mode toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button type="button" onClick={() => handleKeyModeChange('paste')} disabled={loading}
              style={{ padding: '0.35rem 0.75rem', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', background: keyMode === 'paste' ? 'var(--primary)' : 'var(--glass-bg)', color: keyMode === 'paste' ? '#fff' : 'var(--text-primary)', cursor: 'pointer' }}>
              Paste Key
            </button>
            <button type="button" onClick={() => handleKeyModeChange('generate')} disabled={loading}
              style={{ padding: '0.35rem 0.75rem', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', background: keyMode === 'generate' ? 'var(--primary)' : 'var(--glass-bg)', color: keyMode === 'generate' ? '#fff' : 'var(--text-primary)', cursor: 'pointer' }}>
              Generate Key Pair
            </button>
          </div>

          {keyMode === 'paste' ? (
            <textarea
              id="user-ssh-key"
              name="sshPublicKey"
              value={formData.sshPublicKey}
              onChange={handleChange}
              placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAA..."
              className={`ssh-key-textarea ${errors.sshPublicKey ? 'error' : ''}`}
              disabled={loading}
              rows={4}
            />
          ) : (
            <div>
              {!formData.sshPublicKey ? (
                <button type="button" onClick={handleGenerateKey} disabled={generating || loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', background: 'var(--glass-bg)', color: 'var(--text-primary)', cursor: generating ? 'wait' : 'pointer', width: '100%', justifyContent: 'center' }}>
                  <RefreshCw size={14} className={generating ? 'spin' : ''} />
                  {generating ? 'Generating RSA-4096 key pair...' : 'Generate Key Pair'}
                </button>
              ) : (
                <>
                  <textarea
                    value={formData.sshPublicKey}
                    readOnly
                    className="ssh-key-textarea"
                    rows={4}
                    style={{ opacity: 0.8 }}
                  />
                  {generatedPrivateKey && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <button type="button" onClick={handleDownloadPrivateKey}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', border: '2px solid #e67e22', background: privateKeyDownloaded ? '#27ae60' : '#e67e22', color: '#fff', cursor: 'pointer', width: '100%', justifyContent: 'center', fontWeight: 600 }}>
                        <Download size={16} />
                        {privateKeyDownloaded ? '✓ Downloaded — click to download again' : '⬇ Download Private Key (.pem)'}
                      </button>
                      <span style={{ fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--text-secondary)' }}>
                        The private key is generated in your browser and never sent to the server. Download it now — it cannot be retrieved later.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {errors.sshPublicKey && <span className="error-text">{errors.sshPublicKey}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Assigned Folders</label>
          <div className="folder-checkbox-list">
            {availableFolders.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>No active folders available</span>}
            {availableFolders.map(f => (
              <label key={f.id} className="folder-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.folderIds.includes(f.id)}
                  onChange={() => handleFolderToggle(f.id)}
                  disabled={loading}
                />
                {f.name}
              </label>
            ))}
          </div>
        </div>

        {mode === 'edit' && (
          <div className="form-group">
            <label className="form-label">Status</label>
            <div className="active-toggle">
              <button type="button" className={`toggle-switch ${formData.isActive ? 'active' : ''}`} onClick={handleActiveToggle} disabled={loading} />
              <span className="toggle-label">{formData.isActive ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        )}

        {errors.submit && <div className="form-error">{errors.submit}</div>}
      </form>
    </Modal>
  );
}
